import React, { forwardRef, useMemo } from 'react';

// DEPENDENCIES
import { pick } from 'lodash';

// GLOBAL FUNCTIONS
import { formatFunctions } from 'functions.js';
import { DATE } from 'defaults.js';

// LOCAL VARIABLES
const TODAY = new Date();
export const DATE_OPTIONS = {
  all: {
    label: 'All',
    date: null
  },
  custom: {
    label: 'Custom'
  },
  today: {
    label: 'Today',
    date: formatFunctions.date(TODAY)
  },
  yesterday: {
    label: 'Yesterday',
    date: formatFunctions.date(new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate() - 1))
  },
  thisWeek: {
    label: 'This Week',
    startDate: formatFunctions.date(new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate() - TODAY.getDay())),
    endDate:   formatFunctions.date(new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate() - TODAY.getDay() + 6))
  },
  lastWeek: {
    label: 'Last Week',
    startDate: formatFunctions.date(new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate() - TODAY.getDay() - 7)),
    endDate:   formatFunctions.date(new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate() - TODAY.getDay() - 1))
  },
  thisMonth: {
    label: 'This Month',
    startDate: formatFunctions.date(new Date(TODAY.getFullYear(), TODAY.getMonth(), 1)),
    endDate:   formatFunctions.date(new Date(TODAY.getFullYear(), TODAY.getMonth() + 1, 0)),
  },
  lastMonth: {
    label: 'Last Month',
    startDate: formatFunctions.date(new Date(TODAY.getFullYear(), TODAY.getMonth() - 1, 1)),
    endDate:   formatFunctions.date(new Date(TODAY.getFullYear(), TODAY.getMonth(), 0))
  },
  thisQuarter: {
    label: 'This Quarter',
    startDate: formatFunctions.date(new Date(TODAY.getFullYear(), Math.floor(TODAY.getMonth()/3) * 3, 1)),
    endDate:   formatFunctions.date(new Date(TODAY.getFullYear(), Math.floor(TODAY.getMonth()/3) * 3 + 3, 0))
  },
  lastQuarter: {
    label: 'Last Quarter',
    startDate: formatFunctions.date(new Date(TODAY.getFullYear(), Math.floor(TODAY.getMonth()/3) * 3 - 3, 1)),
    endDate:   formatFunctions.date(new Date(TODAY.getFullYear(), Math.floor(TODAY.getMonth()/3) * 3, 0))
  },
  thisYear: {
    label: 'This Year',
    startDate: formatFunctions.date(new Date(TODAY.getFullYear(), 0, 1)),
    endDate:   formatFunctions.date(new Date(TODAY.getFullYear() + 1, 0, 0))
  },
  lastYear: {
    label: 'Last Year',
    startDate: formatFunctions.date(new Date(TODAY.getFullYear() - 1, 0, 1)),
    endDate:   formatFunctions.date(new Date(TODAY.getFullYear(), 0, 0))
  }
}

// MAIN COMPONENT
export const dateOptions = Component => {
  return forwardRef(({
    options,
    dateOptions,
      ...props
  }, ref) => {

    // MEMOS
    const [ useAll, useCustom, useOptions ] = useMemo(
      () => [
        props.useAll || DATE.useAll,
        props.useCustom || DATE.useCustom,
        !props.time && !props.multiple && (props.useOptions || DATE.useOptions),
      ],
      [props]
    )
    const dateOptionsMemo = useMemo(
      () => {
        const dateOptionsArray = ['today', 'yesterday'];
        const rangeOptionsArray = ['today', 'yesterday', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth', 'thisQuarter', 'lastQuarter', 'thisYear', 'lastYear'];
        const optionsArray = Array.isArray(dateOptions) ? dateOptions : props.range ? rangeOptionsArray : dateOptionsArray;
        if (useCustom) optionsArray.unshift('custom');
        if (useAll) optionsArray.push('all');
        return options || pick(DATE_OPTIONS, optionsArray);
      },
      [options, dateOptions, useAll, useCustom, props.range]
    )
    const selectOptionsMemo = useMemo(
      () => Object.keys(dateOptionsMemo).map(key => ({
        label: dateOptionsMemo[key].label,
        value: key
      })),
      [dateOptionsMemo]
    )

    // RENDER
    return <Component
      {...props}
      useAll={useAll}
      useCustom={useCustom}
      useOptions={useOptions}
      dateOptions={dateOptionsMemo}
      selectOptions={selectOptionsMemo}
      ref={ref}
    />
  })
}
